import React, {useState, useEffect, useCallback, useRef} from "react"
import {Box, Skeleton} from "@mui/material"

import {SMSDraftReplyPanel} from "./SMSDraftReplyPanel"
import type {ReplyAction} from "./SMSMessageRow"
import {SMSThread} from "./SMSThread"

import {api} from "@indebted/api"
import type {ConversationMessage, ConversationTimeline} from "@indebted/api"
import {useDraftSMSReply} from "@indebted/api/conversationDraftSMSReplyAPI"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"
import {colors} from "@indebted/theme/colors"

const PAGE_SIZE = 5

function SMSConversationView({
	customerID,
	conversationID,
	preferredCaseID,
	onPickedUp,
}: {
	customerID: string
	conversationID: string
	preferredCaseID?: string
	onPickedUp?: () => void
}) {
	const {notification} = useNotification()
	const {mutateAsync: draftReply} = useDraftSMSReply()
	const [agentEmail, agentName] = useAgent()
	const [timeline, setTimeline] = useState<ConversationTimeline | null>(null)
	const [messages, setMessages] = useState<ConversationMessage[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [selectedReplyMessageId, setSelectedReplyMessageId] = useState<string | null>(null)
	const [pickingUpCase, setPickingUpCase] = useState(false)

	const mounted = useRef(true)
	const loadedCountRef = useRef(0)
	useEffect(() => {
		return () => {
			mounted.current = false
		}
	}, [])

	const loadConversation = useCallback(async () => {
		try {
			// Refetch covers every message already loaded, so it keeps the "Show earlier"
			// history instead of collapsing back to the first page.
			const conversation = await api.customer.getConversation(customerID, conversationID, {
				limit: Math.max(PAGE_SIZE, loadedCountRef.current),
				offset: 0,
				agentEmail,
			})
			if (!mounted.current) return
			setTimeline(conversation)
			setMessages(conversation.messages)
			setHasMore(conversation.hasMore)
			loadedCountRef.current = conversation.messages.length
		} catch {
			if (mounted.current) notification.error("Failed to load the conversation")
		}
	}, [customerID, conversationID, notification, agentEmail])

	useEffect(() => {
		if (!agentEmail) return
		setLoading(true)
		loadConversation().finally(() => {
			if (mounted.current) setLoading(false)
		})
	}, [loadConversation, agentEmail])

	async function pickUpLinkedCase(caseID: string, email: string, name: string) {
		setPickingUpCase(true)
		try {
			await api.customer.pickUpCase(customerID, caseID, {AgentEmail: email, AgentName: name})
			notification.success("Case picked up")
		} catch (error: any) {
			notification.error(error?.Message ?? "Failed to pick up case")
		}
		// Two agents can race for the same case, so both outcomes settle against the server.
		await loadConversation()
		onPickedUp?.()
		if (mounted.current) setPickingUpCase(false)
	}

	function showEarlier() {
		setLoadingMore(true)
		api.customer
			.getConversation(customerID, conversationID, {limit: PAGE_SIZE, offset: messages.length})
			.then((conversation) => {
				if (!mounted.current) return
				setMessages((current) => [...current, ...conversation.messages])
				setHasMore(conversation.hasMore)
				loadedCountRef.current += conversation.messages.length
			})
			.catch(() => {
				if (mounted.current) notification.error("Failed to load earlier messages")
			})
			.finally(() => {
				if (mounted.current) setLoadingMore(false)
			})
	}

	if (loading) {
		return <SMSThreadSkeleton />
	}

	if (!timeline) {
		return null
	}

	const replyTarget = selectedReplyMessageId ?? timeline.replyAnchorProviderMessageId

	// A backend that predates case ownership answers without these fields, and a
	// conversation that cannot be read is worse than one that cannot be gated.
	const linkedCases = timeline.linkedCases ?? []
	const ownershipGated = timeline.caseOwnershipEnabled && linkedCases.length > 0
	const ownsLinkedCase = linkedCases.some((linked) => !!linked.assigneeEmail && linked.assigneeEmail === agentEmail)
	const unassignedLinkedCase =
		linkedCases.find((linked) => linked.caseId === preferredCaseID && !linked.assigneeEmail) ??
		linkedCases.find((linked) => !linked.assigneeEmail)
	const canDraftReply = !ownershipGated || ownsLinkedCase

	const replyActionFor = (message: ConversationMessage): ReplyAction | undefined => {
		if (message.direction !== "inbound") return undefined
		if (canDraftReply) {
			return {kind: "draft", onDraftReply: () => setSelectedReplyMessageId(message.providerMessageId)}
		}
		if (!unassignedLinkedCase) return {kind: "held-by-another-agent"}
		if (!agentEmail || !agentName) return undefined
		return {
			kind: "pick-up",
			pickingUp: pickingUpCase,
			onPickUp: () => void pickUpLinkedCase(unassignedLinkedCase.caseId, agentEmail, agentName),
		}
	}

	return (
		<Box
			sx={{borderTop: `1px solid ${colors.gray["200"]}`, pt: 2.5}}
			display="flex"
			gap={3}
			alignItems="flex-start"
		>
			<Box flex={1} minWidth={0}>
				<SMSThread
					messages={messages}
					replyAnchorProviderMessageId={timeline.replyAnchorProviderMessageId}
					replyTargetProviderMessageId={replyTarget}
					hasMore={hasMore}
					loadingMore={loadingMore}
					onShowEarlier={showEarlier}
					replyActionFor={replyActionFor}
				/>
			</Box>
			{replyTarget && canDraftReply && (
				<Box flex={1} minWidth={0}>
					{/* Keyed on the target so picking a different message starts a fresh
					    draft rather than carrying the last one's instructions over. */}
					<SMSDraftReplyPanel
						key={replyTarget}
						fromNumber={timeline.toAddress}
						toNumber={timeline.fromAddress}
						onGenerateDraft={(request) =>
							draftReply({
								customerID,
								conversationID,
								providerMessageId: replyTarget,
								...request,
							})
						}
					/>
				</Box>
			)}
		</Box>
	)
}

function SMSThreadSkeleton() {
	return (
		<Box display="flex" flexDirection="column" gap={2.5}>
			{[0, 1].map((i) => (
				<Box key={i} p={4} border="1px solid" borderColor="grey.200" borderRadius={1}>
					<Box display="flex" justifyContent="space-between" mb={1.5}>
						<Skeleton variant="text" width={160} />
						<Skeleton variant="rounded" width={72} height={20} />
					</Box>
					<Skeleton variant="text" width="90%" />
				</Box>
			))}
		</Box>
	)
}

export {SMSConversationView}
